import type { mediaEditor as en } from "../en/mediaEditor";
import type { Messages } from "../types";

export const mediaEditor: Messages<typeof en> = {
  open: "تحرير",
  openLabel: "تحرير الصورة",
  title: "تحرير الصورة",
  description: "أضف نصًا، اقتصّ الصورة لإنستغرام، اضبط الألوان أو صِف تعديلًا بالذكاء الاصطناعي. لا يُستبدل الأصل أبدًا: يُحفظ كل تصدير وكل تعديل بالذكاء الاصطناعي كنسخة جديدة.",
  loading: "جارٍ تحميل الصورة…",
  loadFailed: "تعذّر تحميل الصورة للتحرير.",
  canvas: "لوحة الصورة",
  canvasHint: "انقر طبقة لتحديدها، واسحبها لتحريكها، واستخدم المقابض لتغيير الحجم أو التدوير. كل الإجراءات متاحة أيضًا من اللوحات.",
  versionOf: "النسخة {{version}}",
  editedFrom: "معدّلة من النسخة {{version}}",
  toolbar: {
    label: "أدوات المحرر",
    undo: "تراجع",
    redo: "إعادة",
    rotateLeft: "تدوير لليسار",
    rotateRight: "تدوير لليمين",
    flipH: "قلب أفقي",
    flipV: "قلب عمودي",
    shortcuts: "اختصارات لوحة المفاتيح",
    zoom: "التكبير: {{value}}%",
  },
  shortcuts: {
    title: "اختصارات لوحة المفاتيح",
    undo: "تراجع",
    redo: "إعادة",
    delete: "حذف الطبقة المحددة",
    nudge: "تحريك الطبقة المحددة (Shift: ‏10 بكسل)",
    text: "إضافة طبقة نص",
    deselect: "إلغاء التحديد",
  },
  tabs: { text: "النص", adjust: "الضبط", shapes: "الأشكال", layers: "الطبقات", ai: "تعديل بالذكاء الاصطناعي" },
  text: {
    add: "إضافة نص",
    placeholder: "اكتب النص",
    defaultAr: "نص جديد",
    defaultEn: "New text",
    content: "النص",
    font: "الخط",
    weight: "السماكة",
    weights: { "400": "عادي", "500": "متوسط", "600": "شبه عريض", "700": "عريض" },
    size: "الحجم",
    color: "اللون",
    swatches: "ألوان العلامة التجارية",
    swatch: "استخدم {{color}}",
    align: "المحاذاة",
    alignLeft: "يسار",
    alignCenter: "وسط",
    alignRight: "يمين",
    direction: "الاتجاه",
    rtl: "من اليمين لليسار",
    ltr: "من اليسار لليمين",
    background: "صندوق خلفية",
    backgroundColor: "لون الصندوق",
    outline: "إطار",
    outlineColor: "لون الإطار",
    outlineWidth: "سماكة الإطار",
    shadow: "ظل",
    opacity: "الشفافية",
    selectHint: "أضف طبقة نص، أو حدّد واحدة على الصورة أو في الطبقات.",
  },
  adjust: {
    crop: "اقتصاص لإنستغرام",
    ratios: { original: "الأصلي", free: "حر", "1:1": "‏1:1 مربع", "4:5": "‏4:5 طولي", "1.91:1": "‏1.91:1 عرضي", "9:16": "‏9:16 قصة" },
    brightness: "السطوع",
    contrast: "التباين",
    saturation: "التشبع",
    reset: "إعادة ضبط التعديلات",
    output: "الناتج: \u2066{{w}} × {{h}}\u2069 بكسل",
  },
  shapes: {
    rect: "مستطيل",
    ellipse: "شكل بيضاوي",
    stickers: "ملصقات",
    sticker: "إضافة {{emoji}}",
    fill: "التعبئة",
    selectHint: "حدّد شكلًا لتغيير لونه.",
  },
  layers: {
    empty: "لا توجد طبقات بعد. أضف نصًا أو شكلًا أو ملصقًا.",
    select: "تحديد {{name}}",
    up: "نقل للأعلى",
    down: "نقل للأسفل",
    hide: "إخفاء",
    show: "إظهار",
    remove: "حذف الطبقة",
    kinds: { text: "نص", rect: "مستطيل", ellipse: "بيضاوي", sticker: "ملصق" },
  },
  ai: {
    title: "صِف التغيير",
    hint: "يعدّل الذكاء الاصطناعي الصورة الأصلية. يمكنك تلوين المنطقة المطلوب تغييرها؛ تبقى نصوصك وأشكالك فوقها.",
    placeholder: "مثلًا: اجعل الخلفية شاطئًا وقت الغروب",
    paint: "لوّن المنطقة المطلوب تغييرها",
    brush: "حجم الفرشاة",
    clearMask: "مسح المنطقة الملوّنة",
    run: "تطبيق التعديل",
    running: "جارٍ التعديل بالذكاء الاصطناعي…",
    queued: "بدأ التعديل بالذكاء الاصطناعي. يمكنك متابعة التحرير أو المغادرة: سنُعلمك عندما يجهز.",
    done: "التعديل بالذكاء الاصطناعي جاهز: فُتح كنسخة جديدة.",
    failed: "فشل التعديل بالذكاء الاصطناعي.",
    maskHint_zero: "الضربات الملوّنة: {{count}}",
    maskHint_one: "الضربات الملوّنة: {{count}}",
    maskHint_two: "الضربات الملوّنة: {{count}}",
    maskHint_few: "الضربات الملوّنة: {{count}}",
    maskHint_many: "الضربات الملوّنة: {{count}}",
    maskHint_other: "الضربات الملوّنة: {{count}}",
  },
  export: {
    button: "تصدير",
    png: "PNG (بلا فقد)",
    jpeg: "JPEG (حجم أصغر)",
    format: "الصيغة",
    saving: "جارٍ الحفظ…",
    saved: "حُفظت في مكتبة الوسائط كالنسخة {{version}}",
    failed: "فشل التصدير. حاول مرة أخرى.",
    usePost: "استخدمها في منشور",
    another: "متابعة التحرير",
  },
  close: "إغلاق المحرر",
  discardTitle: "إغلاق المحرر؟",
  /** ALP-84: الإغلاق مع تعديلات لم تُصدَّر. */
  discard: {
    title: "تجاهل تعديلاتك؟",
    hint: "لم تصدّرها بعد. تبقى الصورة الأصلية كما هي.",
    confirm: "تجاهل التعديلات",
  },
};
